import React from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../../lib/api";

// Broker Confirmations Panel — closes GW-6 reciprocal end-to-end.
// The admin can see every confirmation-back attempt with status, HTTP code,
// broker, and response snippet. Retry-safe for HTTP failures.

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

const statusTone = (s) => {
  switch ((s || "").toLowerCase()) {
    case "sent":       return "bg-forest/10 text-forest";
    case "http_error": return "bg-amber-50 text-amber-700";
    case "error":      return "bg-rust/10 text-rust";
    case "skipped":    return "bg-slate-100 text-slate-500";
    default:           return "bg-slate-100 text-slate-500";
  }
};

export default function BrokerConfirmationsPanel() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [retrying, setRetrying] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);
  const [brokerOrgs, setBrokerOrgs] = React.useState([]);
  // ?filter=unconfirmed — Trust Ops → Broker POD-back "?" deep-links here.
  // Filters the table to rows whose status is NOT "sent" (retryable failures
  // + pending, which is where the operator can move the needle).
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [r, orgs] = await Promise.all([
        apiClient.get(`/admin/broker-confirmations`, { params: { limit: 100 } }),
        apiClient.get(`/admin/organizations`, { params: { type: "broker" } }),
      ]);
      const body = r.data?.data || r.data || {};
      setRows(body.items || []);
      const ob = orgs.data?.data || orgs.data || [];
      setBrokerOrgs(Array.isArray(ob) ? ob : (ob.items || []));
    } catch (e) {
      setError(String(e?.response?.data?.error?.message || e?.response?.data?.detail || e?.message || "Failed to load"));
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const retry = async (tripId) => {
    setRetrying(tripId);
    try {
      await apiClient.post(`/admin/broker-confirmations/${tripId}/send`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error?.message || e?.response?.data?.detail || e?.message || "Retry failed");
    } finally { setRetrying(null); }
  };

  return (
    <section
      className="rounded-2xl border border-[#E2E0D8] bg-white p-5 astat-fade-in"
      data-testid="broker-confirmations-panel"
    >
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] text-forest/70 uppercase">
            GW-6 · Broker Reciprocal
          </div>
          <h2 className="text-xl font-bold text-slate_navy" data-testid="broker-conf-title">
            Broker confirmations
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Every trip we complete is auto-POSTed back to the originating broker.
            Signed HMAC-SHA256. Failed sends can be retried here.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="broker-conf-refresh"
          className="text-sm px-3 py-1.5 border border-[#E2E0D8] rounded-md hover:bg-forest/5"
        >
          ↻ Refresh
        </button>
      </header>

      {error && (
        <div className="mb-3 rounded-lg border border-rust/50 bg-rust/5 px-3 py-2 text-sm text-rust" data-testid="broker-conf-error">
          {error}
        </div>
      )}

      {/* Broker POD-back webhook editor — closes the "no shell required" loop */}
      <div className="mb-5 rounded-lg border border-[#E2E0D8] bg-bone/40 p-4" data-testid="broker-conf-editor">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
          Broker POD-back webhook URLs
        </div>
        {brokerOrgs.length === 0 ? (
          <div className="text-xs text-slate-500">No broker organizations registered yet.</div>
        ) : (
          <div className="space-y-2">
            {brokerOrgs.map((o) => (
              <BrokerUrlRow key={o.id} org={o} onSaved={load} />
            ))}
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#E2E0D8]">
        {filter === "unconfirmed" && (
          <div
            className="flex items-center gap-3 px-4 py-2 border-b border-amber-300 bg-amber-50 text-sm text-amber-800"
            data-testid="broker-conf-filter-banner"
          >
            <span className="font-bold">Filtered:</span>
            <span>Unconfirmed POD-backs only — from Trust Ops → Broker POD-back</span>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              data-testid="broker-conf-filter-clear"
              className="ml-auto font-semibold text-xs underline"
            >Clear filter</button>
          </div>
        )}
        <table className="min-w-full text-sm" data-testid="broker-conf-table">
          <thead className="bg-bone/60 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 w-[110px]">When</th>
              <th className="text-left px-3 py-2">Broker</th>
              <th className="text-left px-3 py-2 w-[130px]">Status</th>
              <th className="text-left px-3 py-2 w-[90px]">HTTP</th>
              <th className="text-left px-3 py-2">Trip</th>
              <th className="text-right px-3 py-2 w-[110px]" />
            </tr>
          </thead>
          <tbody className="astat-fade-stagger" key={rows.length}>
            {loading && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400" data-testid="broker-conf-loading">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400" data-testid="broker-conf-empty">
                No confirmations sent yet. When brokers post trips and drivers complete them, entries will show here.
              </td></tr>
            )}
            {(() => {
              const displayed = filter === "unconfirmed"
                ? rows.filter(r => (r.status || "").toLowerCase() !== "sent")
                : rows;
              if (!loading && displayed.length === 0 && rows.length > 0 && filter) {
                return (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400" data-testid="broker-conf-filter-empty">
                    No unconfirmed POD-backs — everything is sent. Nice work.
                  </td></tr>
                );
              }
              return displayed.map((r) => {
              const isOpen = expanded === r.id;
              const isRetryable = ["http_error", "error"].includes(r.status);
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-[#E2E0D8] hover:bg-slate-50" data-testid={`broker-conf-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtWhen(r.at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate_navy">{r.broker_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]" title={r.url}>{r.url || "no URL configured"}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={"inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider " + statusTone(r.status)}>
                        {r.status}
                      </span>
                      {r.retry && <span className="ml-1 text-[10px] text-slate-500">·retry</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.http_status || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600" title={r.trip_id}>
                      {(r.trip_id || "").slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(r.response_snippet || r.error) && (
                        <button
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          data-testid={`broker-conf-details-${r.id}`}
                          className="text-xs text-forest hover:underline mr-2"
                        >
                          {isOpen ? "Hide" : "Details"}
                        </button>
                      )}
                      {isRetryable && (
                        <button
                          onClick={() => retry(r.trip_id)}
                          disabled={retrying === r.trip_id}
                          data-testid={`broker-conf-retry-${r.id}`}
                          className="text-xs font-bold px-3 py-1 rounded-full bg-forest text-white disabled:opacity-60"
                        >
                          {retrying === r.trip_id ? "…" : "Retry"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-bone/40 border-t border-[#E2E0D8]">
                      <td colSpan={6} className="px-3 py-3">
                        {r.error && (
                          <div className="text-xs text-rust mb-2 font-mono">Error: {r.error}</div>
                        )}
                        {r.response_snippet && (
                          <pre className="whitespace-pre-wrap break-all text-slate_navy font-mono text-[11px] leading-tight max-h-[280px] overflow-y-auto rounded bg-white border border-[#E2E0D8] p-2">
                            {r.response_snippet}
                          </pre>
                        )}
                        {r.reason && (
                          <div className="text-xs text-slate-500 italic mt-1">{r.reason}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Showing {filter === "unconfirmed" ? rows.filter(r => (r.status || "").toLowerCase() !== "sent").length : rows.length} · newest first
      </div>
    </section>
  );
}

// Per-org URL + secret editor. Stripped-down: one row per broker, in-place save.
function BrokerUrlRow({ org, onSaved }) {
  const [url, setUrl] = React.useState(org.broker_confirmation_url || "");
  const [secret, setSecret] = React.useState(""); // never round-trip the secret; blank = don't change
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const patch = { broker_confirmation_url: url || null };
      if (secret) patch.broker_confirmation_secret = secret;
      await apiClient.patch(`/admin/organizations/${org.id}`, patch);
      setSecret("");
      setMsg("Saved");
      onSaved?.();
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg(e?.response?.data?.error?.message || e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const configured = !!org.broker_confirmation_url;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`broker-url-row-${org.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold text-slate_navy">{org.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {configured ? "webhook configured" : "no webhook — trips are logged only"}
        </div>
      </div>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://broker.example.com/medexova/pod"
        data-testid={`broker-url-input-${org.id}`}
        className="flex-[2] min-w-[280px] rounded-md border border-[#E2E0D8] px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-forest/30"
      />
      <input
        type="password"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        placeholder={org.broker_confirmation_url ? "secret (leave blank to keep)" : "HMAC secret (optional)"}
        data-testid={`broker-secret-input-${org.id}`}
        className="w-[220px] rounded-md border border-[#E2E0D8] px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-forest/30"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`broker-url-save-${org.id}`}
        className="text-xs font-bold px-3 py-1.5 rounded-md bg-forest text-white disabled:opacity-60"
      >
        {saving ? "…" : "Save"}
      </button>
      {msg && <span className="text-xs text-forest">{msg}</span>}
    </div>
  );
}
